import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { ContentCopy } from '@mui/icons-material'
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined'
import { Chip, Grid, IconButton, Tooltip, Typography, useTheme } from '@mui/material'

import { useAuthentication } from '../../providers/auth-provider'
import { WRITE_PERMISSIONS_LIST } from '../../utils/constants/permissions'
import { getFormattedDate } from '../../utils/helpers'
import useClipboard from '../../utils/hooks/useClipboard'
import useSharedStyles from '../../utils/shared.styles'

import useStyles from './index.styles'

interface IProps {
  releaseNote: IReleaseNoteType
}

const createId = (title: string): string => title.trim().toLowerCase().replace(/\s+/g, '-')

export const hasWriteReleasePermission = (userPermissions: IPermissionType[]): boolean =>
  userPermissions && userPermissions.length ? userPermissions.find((item) => item.id === WRITE_PERMISSIONS_LIST.releases.id) !== undefined : false

const ReleaseNote = (props: IProps) => {
  const { releaseNote } = props

  const classes = useStyles
  const sharedClasses = useSharedStyles
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { clickToCopy } = useClipboard()

  const { ownPermissions } = useAuthentication()

  const [titleHovered, setTitleHovered] = useState<boolean>(false)

  useEffect(() => {
    if (location.hash) {
      // to scroll to a release not with provided id
      const element = document.querySelector(location.hash)
      if (element) {
        const elementPosition = element.getBoundingClientRect().top + window.scrollY
        const offsetPosition = elementPosition - 100

        window.scrollTo({ behavior: 'smooth', top: offsetPosition })
      }
    }
  }, [location])

  return (
    <Grid size={12} container={true} spacing={1} id={createId(releaseNote.title)}>
      <Grid size={12}>
        <Chip
          label={releaseNote.level}
          sx={{
            ...classes.releaseChip(theme),
            ...(releaseNote.level === 'major' && classes.majorChip(theme)),
            ...(releaseNote.level === 'minor' && classes.minorChip(theme)),
          }}
        />
      </Grid>
      <Grid size={11}>
        <Typography variant="h6" onMouseOver={() => setTitleHovered(true)} onMouseLeave={() => setTitleHovered(false)}>
          {releaseNote?.title}
          <span style={classes.date(theme)}>({getFormattedDate(releaseNote.releaseDate)})</span>

          {titleHovered && (
            <Tooltip title="Copy link to clipboard" arrow={true} placement="top">
              <IconButton
                onClick={() => {
                  clickToCopy(`${window.location.origin}/release-notes#${createId(releaseNote.title)}`)
                }}
                aria-label="Copy link to clipboard"
                sx={classes.copyIconButton}
              >
                <ContentCopy color="primary" sx={classes.copyIcon} />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
      </Grid>
      <Grid size={1}>
        {hasWriteReleasePermission(ownPermissions) && (
          <IconButton sx={{ marginLeft: 1 }} onClick={() => navigate(`/release-notes/${releaseNote.id}`)} aria-label="Edit Release Note">
            <ModeEditOutlinedIcon color="primary" />
          </IconButton>
        )}
      </Grid>
      <Grid size={11}>
        <ol style={classes.contentList(theme)}>
          {releaseNote.notes.map((note, idx) => (
            <li key={idx}>
              <Typography variant="subtitle2">{note.header}</Typography>

              <Typography variant="body2" sx={sharedClasses.secondaryText}>
                {note.content}
              </Typography>
            </li>
          ))}
        </ol>
      </Grid>
    </Grid>
  )
}

export default ReleaseNote
